/**
 * Transactions Redux Slice
 * 
 * Clean state management for transaction list with filtering and pagination.
 * Follows single responsibility - only manages transaction list state.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import type {
  TransactionResponse,
  TransactionQueryParams,
  TransactionListResponse,
} from '@/app/types/transactions';
import { transactionService } from '@/app/services/transactionService';

// ============================================================================
// Types
// ============================================================================

interface TransactionsState {
  items: TransactionResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: TransactionQueryParams;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// ============================================================================
// Constants
// ============================================================================

// Page size options available to users
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];

// Default page size - can be overridden by env var in future
const DEFAULT_PAGE_SIZE: PageSizeOption = 10;

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: TransactionQueryParams = {
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

const initialState: TransactionsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: initialFilters,
  status: 'idle',
  error: null,
};

// ============================================================================
// Async Thunks
// ============================================================================

export const fetchTransactions = createAsyncThunk<
  TransactionListResponse,
  TransactionQueryParams | undefined,
  { state: RootState; rejectValue: string }
>(
  'transactions/fetch',
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = params ?? state.transactions.filters;
      return await transactionService.queryTransactions(filters);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch transactions'
      );
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<TransactionQueryParams>>) => {
      // Merge new filters with existing, reset to page 1
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0, // Reset pagination when filters change
      };
      state.currentPage = 1;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      const page = action.payload;
      state.currentPage = page;
      state.filters.offset = (page - 1) * state.pageSize;
    },
    
    setPageSize: (state, action: PayloadAction<PageSizeOption>) => {
      const newSize = action.payload;
      state.pageSize = newSize;
      state.filters.limit = newSize;
      state.filters.offset = 0;
      state.currentPage = 1;
    },
    
    clearFilters: (state) => {
      state.filters = { ...initialFilters, limit: state.pageSize };
      state.currentPage = 1;
    },
    
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.transactions;
        state.totalCount = action.payload.total;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

// ============================================================================
// Actions & Selectors
// ============================================================================

export const { setFilters, setPage, setPageSize, clearFilters, resetState } = transactionsSlice.actions;

// Selectors
export const selectTransactions = (state: RootState) => state.transactions.items;
export const selectTransactionsTotalCount = (state: RootState) => state.transactions.totalCount;
export const selectTransactionsCurrentPage = (state: RootState) => state.transactions.currentPage;
export const selectTransactionsPageSize = (state: RootState) => state.transactions.pageSize;
export const selectTransactionsFilters = (state: RootState) => state.transactions.filters;
export const selectTransactionsStatus = (state: RootState) => state.transactions.status;
export const selectTransactionsError = (state: RootState) => state.transactions.error;

// Derived selectors
export const selectTransactionsIsLoading = (state: RootState) => 
  state.transactions.status === 'loading';

export const selectTransactionsTotalPages = (state: RootState) => 
  Math.ceil(state.transactions.totalCount / state.transactions.pageSize);

export default transactionsSlice.reducer;

